import { Component } from '@theme/component';
import { trapFocus, removeTrapFocus } from '@theme/focus';
import { onAnimationEnd } from '@theme/utilities';

/**
 * Hide the page behind the open drawer from assistive technology (and from
 * clicks and focus), so a screen reader's virtual cursor can't wander out of
 * the menu. The header itself stays live: the drawer lives inside it.
 * @param {boolean} inert
 */
function setPageInert(inert) {
  for (const element of document.querySelectorAll('#MainContent, #MainContent ~ footer')) {
    if (element instanceof HTMLElement) element.inert = inert;
  }
}

/**
 * A custom element that manages the main menu drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDetailsElement} details - The details element.
 * @property {HTMLDivElement} menuDrawer - The slideable drawer panel containing the menu.
 *
 * @extends {Component<Refs>}
 */
class HeaderDrawer extends Component {
  requiredRefs = ['details', 'menuDrawer'];

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('keyup', this.#onKeyUp);
    this.refs.details.addEventListener('toggle', this.#handleDetailsToggle);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('keyup', this.#onKeyUp);
    this.refs.details.removeEventListener('toggle', this.#handleDetailsToggle);
  }

  /**
   * Clears `data-drawer-header-pinned` in response to the details element's own native
   * `toggle` event, rather than eagerly inside #close()'s onAnimationEnd callback.
   *
   * The `open` attribute reflects synchronously the instant `reset()` (below) calls
   * `removeAttribute('open')`, but per the HTML spec a `<details>` element's `toggle` event
   * is always fired via a *queued task* - never synchronously, even for a script-driven
   * attribute change. `scroll-lock` (dialog.js's own `toggle` listener, on `document` with
   * `capture: true`, mirroring this same event) is removed from `<html>` only once that
   * queued task actually runs. Removing `data-drawer-header-pinned` eagerly, synchronously,
   * right inside `reset()`'s caller, was therefore removing it a full task *before*
   * `scroll-lock` - a real window where `html[scroll-lock]` was still forcing `overflow:
   * hidden` (see base.css) while the compensating `[data-drawer-header-pinned]` override in
   * sections/header.liquid had already stopped forcing the header-section fixed. Exactly the
   * failure #snapshotHeaderPinnedState's own docstring describes (a stuck header reverting to
   * its natural, unstuck, possibly off-screen document position while position: sticky's
   * scrolling context is still frozen) - just at the *closing* edge instead of the opening
   * one it was written to guard against. Listening for the same `toggle` event here instead
   * guarantees this only ever clears at or after `scroll-lock` does, never before it.
   */
  #handleDetailsToggle = () => {
    if (this.refs.details.open) return;
    document.documentElement.removeAttribute('data-drawer-header-pinned');
    setPageInert(false);
  };

  /**
   * Close the innermost open level when the Escape key is pressed: the open
   * submenu if focus is inside one, otherwise the drawer itself. Looked up
   * as the closest *open* details, since focus often sits on the summary of
   * a closed submenu, whose own details is the closest one.
   * @param {KeyboardEvent} event
   */
  #onKeyUp = (event) => {
    if (event.key !== 'Escape') return;

    const target = event.target instanceof Element ? event.target : null;
    const details = target?.closest('details[open]') ?? this.refs.details;
    if (!this.contains(details) || !details.hasAttribute('open')) return;

    this.#close(details);
  };

  /**
   * @returns {boolean} Whether the main menu drawer is open
   */
  get isOpen() {
    return this.refs.details.hasAttribute('open');
  }

  /**
   * Get the closest details element to the event target
   * @param {Event | undefined} event
   * @returns {HTMLDetailsElement}
   */
  #getDetailsElement(event) {
    if (!(event?.target instanceof Element)) return this.refs.details;

    return event.target.closest('details') ?? this.refs.details;
  }

  /**
   * Toggle the main menu drawer
   * @param {Event} [event]
   */
  toggle(event) {
    // A <summary> click natively toggles its <details>' `open` attribute the
    // moment this handler returns, regardless of what we do below. Since
    // `isOpen`/`#close()` treat that attribute as the source of truth (and
    // `#close()` deliberately delays removing it until the close animation
    // finishes), that native toggle firing on its own timeline desyncs the
    // attribute from our `menu-open` class after a couple of rapid clicks —
    // e.g. closing gets its `open` attribute stripped mid-animation, cutting
    // the transition short. Prevent it so `open()`/`#close()` are the only
    // code that ever changes `open`.
    event?.preventDefault();

    return this.isOpen ? this.close() : this.open();
  }

  /**
   * Open the closest drawer or the main menu drawer
   * @param {string} [target]
   * @param {Event} [event]
   */
  open(target, event) {
    // Same reasoning as toggle() above — this is also reached from a
    // <summary> click (nested submenu triggers), so block its native toggle
    // and set `open` ourselves so it can't race with our own state.
    event?.preventDefault();

    const details = this.#getDetailsElement(event);
    const summary = details.querySelector('summary');

    if (!summary) return;

    details.setAttribute('open', '');

    if (!target && details === this.refs.details) {
      this.#snapshotHeaderPinnedState();
      setPageInert(true);
    }

    if (target) {
      this.#advanceNavButtons(details);
    }

    summary.setAttribute('aria-expanded', 'true');

    this.preventInitialAccordionAnimations(details);
    requestAnimationFrame(() => {
      details.classList.add('menu-open');

      if (target) {
        this.refs.menuDrawer.classList.add('menu-drawer--has-submenu-opened');
      }

      // Wait for the drawer animation to complete before trapping focus
      const drawer = details.querySelector('.menu-drawer, .menu-drawer__submenu');
      onAnimationEnd(drawer || details, () => trapFocus(details), { subtree: false });
    });
  }

  /**
   * Go back or close the main menu drawer
   * @param {Event} [event]
   */
  back(event) {
    this.#close(this.#getDetailsElement(event));
  }

  /**
   * Close the main menu drawer
   */
  close() {
    this.#close(this.refs.details);
  }

  /**
   * Close the closest menu or submenu that is open
   *
   * @param {HTMLDetailsElement} details
   */
  #close(details) {
    const summary = details.querySelector('summary');

    if (!summary) return;

    this.#retreatNavButtons(details);

    summary.setAttribute('aria-expanded', 'false');
    details.classList.remove('menu-open');
    this.refs.menuDrawer.classList.remove('menu-drawer--has-submenu-opened');

    // Wait for the .menu-drawer element's transition, not the entire details subtree
    // This avoids waiting for child accordion/resource-card animations which can cause issues on Firefox
    const drawer = details.querySelector('.menu-drawer, .menu-drawer__submenu');

    onAnimationEnd(
      drawer || details,
      () => {
        reset(details);
        if (details === this.refs.details) {
          removeTrapFocus();
          setPageInert(false);
          const openDetails = this.querySelectorAll('details[open]:not(accordion-custom > details)');
          openDetails.forEach(reset);
          this.#resetNavButtons();
        } else {
          trapFocus(this.refs.details);
        }
        // Hand focus back to the summary that opened this level: the control
        // that had it (back/close button, a link inside) is now hidden, and
        // focus would otherwise fall to <body>.
        summary.focus({ preventScroll: true });
      },
      { subtree: false }
    );
  }

  /**
   * Snapshots whether the header is currently stuck (scrolled past the
   * announcement bar, `data-sticky-state="active"`) into a `<html>`
   * attribute for as long as this drawer stays open.
   *
   * `scroll-lock` (see header-drawer.liquid) applies `overflow: hidden` to
   * <html> for that same window, which breaks `position: sticky` — browsers
   * stop treating an overflow:hidden root as a scrolling context for it, so
   * a stuck header would revert to its natural, unstuck document position
   * (off-screen above the viewport if the page was scrolled). header.liquid
   * and header-drawer.liquid's mobile CSS both read this snapshot to force
   * the header back to a fixed, pinned position and size the drawer to only
   * the header's own height in that case — but `data-sticky-state` itself
   * can't be read live for this: forcing the header fixed makes the
   * IntersectionObserver driving it see the header as fully visible again
   * and flip it back to inactive, which would immediately undo the very fix
   * that made it visible. Reading it once, before locking scroll, avoids
   * that feedback loop.
   */
  #snapshotHeaderPinnedState() {
    const header = document.querySelector('#header-component');
    document.documentElement.toggleAttribute(
      'data-drawer-header-pinned',
      header?.getAttribute('data-sticky-state') === 'active'
    );
  }

  /**
   * A menu-dropdown's .menu-drawer__nav-buttons (blocks/menu-dropdown.liquid)
   * is the only one rendered for that top-level item and everything nested
   * under it — a deeper menu-link (blocks/menu-link.liquid) doesn't render
   * its own copy. Opening a nested level moves that single bar to be the
   * first child of the newly-opened level's own .menu-drawer__inner-submenu
   * (so its back button's closest('details') resolves to the right level —
   * same reparenting idea as header-menu.js's attachDrawerHeader/Footer for
   * the desktop drawer) and appends " / <label>" to its text. The
   * pre-advance label is snapshotted onto `details.dataset` first so
   * #retreatNavButtons can restore it verbatim instead of parsing it back
   * out — a label that itself contains "/" would otherwise break that
   * parse. Top-level opens are a no-op: the bar already lives there.
   * @param {HTMLElement} details
   */
  #advanceNavButtons(details) {
    if (!details.classList.contains('menu-link--mobile')) return;

    const parentInnerSubmenu = details.parentElement?.closest('.menu-drawer__inner-submenu');
    const navButtons = parentInnerSubmenu?.querySelector(':scope > .menu-drawer__nav-buttons');
    const innerSubmenu = details.querySelector(':scope > .menu-drawer__submenu > .menu-drawer__inner-submenu');

    if (!navButtons || !innerSubmenu || innerSubmenu.firstElementChild === navButtons) return;

    innerSubmenu.insertBefore(navButtons, innerSubmenu.firstChild);

    const label = navButtons.querySelector('.menu-drawer__nav-buttons-label');
    if (label && details.dataset.drawerLabel) {
      details.dataset.breadcrumbBeforeLabel = label.textContent ?? '';
      label.textContent = `${label.textContent} / ${details.dataset.drawerLabel}`;
    }
  }

  /**
   * Reverses #advanceNavButtons when a nested level closes (via its own back
   * button or Escape): moves the shared bar back out to the parent level's
   * .menu-drawer__inner-submenu and restores the pre-advance label that
   * #advanceNavButtons snapshotted onto `details.dataset`, rather than
   * parsing the trailing " / <label>" segment back off — which would break
   * for labels containing "/". Top-level closes are a no-op here — full
   * cleanup for those happens in #resetNavButtons instead, since the whole
   * drawer (and everything nested inside it) is resetting together.
   * @param {HTMLElement} details
   */
  #retreatNavButtons(details) {
    if (!details.classList.contains('menu-link--mobile')) return;

    const innerSubmenu = details.querySelector(':scope > .menu-drawer__submenu > .menu-drawer__inner-submenu');
    const navButtons = innerSubmenu?.querySelector(':scope > .menu-drawer__nav-buttons');
    const parentInnerSubmenu = details.parentElement?.closest('.menu-drawer__inner-submenu');

    if (!navButtons || !parentInnerSubmenu) return;

    parentInnerSubmenu.insertBefore(navButtons, parentInnerSubmenu.firstChild);

    const label = navButtons.querySelector('.menu-drawer__nav-buttons-label');
    if (label && details.dataset.breadcrumbBeforeLabel !== undefined) {
      label.textContent = details.dataset.breadcrumbBeforeLabel;
      delete details.dataset.breadcrumbBeforeLabel;
    }
  }

  /**
   * Rehomes every .menu-drawer__nav-buttons bar back to its own top-level
   * menu-dropdown and restores its original label, for whenever the whole
   * drawer closes (Escape, the drawer's own close button, or the backdrop)
   * without walking back out one level at a time first — #retreatNavButtons
   * only ever undoes one level per call, so a shortcut close would otherwise
   * leave a bar stranded deep in the tree with a stale breadcrumb label.
   */
  #resetNavButtons() {
    const allNavButtons = this.querySelectorAll('.menu-drawer__nav-buttons');

    allNavButtons.forEach((navButtons) => {
      const topLevelDetails = navButtons.closest('.menu-drawer__menu-container:not(.menu-link--mobile)');
      if (!topLevelDetails) return;

      const homeInnerSubmenu = topLevelDetails.querySelector(':scope > .menu-drawer__submenu > .menu-drawer__inner-submenu');
      if (homeInnerSubmenu && homeInnerSubmenu.firstElementChild !== navButtons) {
        homeInnerSubmenu.insertBefore(navButtons, homeInnerSubmenu.firstChild);
      }

      const label = navButtons.querySelector('.menu-drawer__nav-buttons-label');
      if (label && topLevelDetails.dataset.drawerLabel) {
        label.textContent = topLevelDetails.dataset.drawerLabel;
      }
    });
  }

  /**
   * Temporarily disables accordion animations to prevent unwanted transitions when the drawer opens.
   * Adds a no-animation class to accordion content elements, then removes it after 100ms to
   * re-enable animations for user interactions.
   * @param {HTMLDetailsElement} details - The details element containing the accordions
   */
  preventInitialAccordionAnimations(details) {
    const content = details.querySelectorAll('accordion-custom .details-content');

    content.forEach((element) => {
      if (element instanceof HTMLElement) {
        element.classList.add('details-content--no-animation');
      }
    });
    setTimeout(() => {
      content.forEach((element) => {
        if (element instanceof HTMLElement) {
          element.classList.remove('details-content--no-animation');
        }
      });
    }, 100);
  }
}

if (!customElements.get('header-drawer')) {
  customElements.define('header-drawer', HeaderDrawer);
}

/**
 * Reset an open details element to its original state
 *
 * @param {HTMLDetailsElement} element
 */
function reset(element) {
  element.classList.remove('menu-open');
  element.removeAttribute('open');
  element.querySelector('summary')?.setAttribute('aria-expanded', 'false');
}
